import { DownloadSimpleIcon, EyeIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import { endOfDay, startOfDay } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { useExportVisitorHistory } from '@/api/visitors/export-visitor-history'
import { useGetVisitorCountsByClient } from '@/api/visitors/get-visitor-counts-by-client'
import { useGetVisitorHistory } from '@/api/visitors/get-visitor-history'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select, SelectOption } from '@/components/ui/select'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import LegacyHistory from '@/components/Visitors/History/History'
import { popupFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange, VisitorHistoryModel } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { buildCsvFilename } from '@/utils'

import { GuestDetailsModal } from '../guest-details-modal'
import { VendorDetailsModal } from '../vendor-details-modal'
import { VisitorsDashboardOutletContext } from '../visitors-dashboard.page.types'
import { useDefaultVisitorsSort } from '../visitors-dashboard.utils'
import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  getHistoryColumns,
  visitorsHistoryFiltersSchema,
  visitorsHistoryInitialFilters,
} from './visitors-history.constants'
import * as S from './visitors-history.styled'

export const VisitorsHistoryPage = () => {
  const { t } = useTranslation(['visitors'])
  const { uiVersion } = useOutletContext<VisitorsDashboardOutletContext>()
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? 'America/Chicago'

  const searchParams = useSearchParams({
    initialState: visitorsHistoryInitialFilters,
    schema: visitorsHistoryFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: visitorsHistoryInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  useDefaultVisitorsSort({ filters, searchParams })

  const fromDate = filters.getFilter('fromDate')
  const toDate = filters.getFilter('toDate')
  const search = filters.getFilter('search')
  const clientId = filters.getFilter('clientId')
  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')
  const [residentsSearch, setResidentsSearch] = useState('')

  const dateRange: DateRange = [
    fromDate ? startOfDay(toZonedTime(fromDate, timezone)) : null,
    toDate ? startOfDay(toZonedTime(toDate, timezone)) : null,
  ]

  const {
    data,
    isFetching: isHistoryFetching,
    error: historyError,
  } = useGetVisitorHistory({
    buildingId,
    clientId,
    direction: sortOrder,
    end: toDate,
    limit: filters.getFilter('limit'),
    search: search || undefined,
    skip: filters.getFilter('skip'),
    sort: sortBy,
    start: fromDate,
  })

  const { history = [], count = 0 } = data ?? {}

  const { data: residentsData, isFetching: isResidentsFetching } = useGetVisitorCountsByClient({
    buildingId,
    end: toDate,
    search: residentsSearch,
    start: fromDate,
    type: 'visitor',
  })

  const residents = residentsData?.data
  const residentsOptions: SelectOption<number>[] =
    residents?.map((client) => ({
      label: `${client.name} - (${client.count})`,
      value: client.clientId,
    })) || []

  const { mutateAsync: downloadHistory, isPending: isExporting } = useExportVisitorHistory()

  const handleExportCSV = () => {
    if (!buildingId) return
    void downloadHistory({
      buildingId,
      clientId,
      direction: sortOrder,
      end: toDate,
      fileName: buildCsvFilename({
        building: activeBuilding?.name,
        dateRange: [fromDate ?? null, toDate ?? null],
        page: 'Visitor History',
        timezone,
      }),
      search: search || undefined,
      sort: sortBy,
      start: fromDate,
    })
  }

  const columns = getHistoryColumns({ t, timezone })

  const rowActions: RowAction<VisitorHistoryModel>[] = [
    {
      icon: EyeIcon,
      label: t('visitors:columns.details'),
      onClick: (item) =>
        item.type === 'vendor'
          ? popupFactory.open(VendorDetailsModal, { vendorId: item.visitorId })
          : popupFactory.open(GuestDetailsModal, { guestId: item.visitorId }),
      primary: true,
    },
  ]

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({
      fromDate: range[0] ? fromZonedTime(startOfDay(range[0]), timezone).getTime() : undefined,
      toDate: range[1] ? fromZonedTime(endOfDay(range[1]), timezone).getTime() : undefined,
    })
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleResidentChange = (clientIds: number[]) => {
    filters.setFilter('clientId', clientIds)
  }

  const handleSort = (newSortBy: string | null, newSortOrder: SortOrder | null) => {
    filters.setFilters((prevFilters) => ({
      ...prevFilters,
      sortBy: newSortBy ?? undefined,
      sortOrder: newSortOrder ?? undefined,
    }))
  }

  return uiVersion === 'legacy' ? (
    <LegacyHistory />
  ) : (
    <>
      <TableGroup
        id="visitors-history"
        columns={columns}
        data={history}
        error={historyError}
        getRowKey={(item) => `${item.visitorId}-${item.date}`}
        loading={isHistoryFetching}
        mode="regular"
        rowActions={rowActions}
        tableFilters={filters}
        emptyNoFilters={{
          description: t('visitors:dashboard.history.emptyDescription'),
          title: t('visitors:dashboard.history.emptyTitle'),
        }}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={isExporting}
              size="small"
              title={t('visitors:dashboard.actions.exportCsv')}
              use="secondary"
              onClick={handleExportCSV}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <DatePickerInput
              mode="range"
              placeholder={t('visitors:toolbar.selectDateRange')}
              size="small"
              value={dateRange}
              maxDate={toZonedTime(new Date(), timezone)}
              onRangeChange={handleDateChange}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              placeholder={t('visitors:dashboard.filters.historySearchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
            />
          ),
          // Note: Exception - the `statusTabs` slot is repurposed here to hold the resident
          // filter since History has no status tabs. Don't follow this pattern elsewhere;
          // this slot should normally hold actual status tabs.
          statusTabs: (
            <S.ResidentFilterWrapper>
              <Select
                searchable
                disabled={isResidentsFetching}
                dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
                options={residentsOptions}
                placeholder={t('visitors:dashboard.filters.residentPlaceholder')}
                size="small"
                type="multi"
                value={clientId ?? []}
                onChange={handleResidentChange}
                onReset={() => handleResidentChange([])}
                onSearchChange={setResidentsSearch}
              />
            </S.ResidentFilterWrapper>
          ),
        }}
        pagination={{
          paginationFilters: filters,
          total: count,
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
    </>
  )
}
